import type { AtLeast } from '@vartion/ui'
import { acceptHMRUpdate, defineStore } from 'pinia'

import { type User } from '~/types.ts'

export const useUsers = defineStore('users', () => {
  const users = reactive<Record<number, User>>({})
  const deletedUsers = reactive<Record<number, User>>({})
  const cachedUsers = reactive<Record<number, User>>({})
  const onlineUsers = new Set<number>()

  const userIdsWithoutTransactionMonitoringPermission = computed(() => {
    return new Set(
      Object.values(users)
        .filter((user) => !user.roles.some((role) => role.permissions.some((permission) => permission.name === 'modules.transactionMonitoring')))
        .map((user) => user.id),
    )
  })

  async function update(user: AtLeast<User, 'id'> & { group_ids?: number[] }) {
    const { data } = await api.patch<User>(`users/${user.id}`, user)

    if (user.id === stores.user.user.id) {
      stores.user._update(data)
    }

    _update(data)
  }

  async function fetch() {
    const { data } = await api.get(`organizations/${stores.organization.id}`)
    stores.users.set(data.users)
    stores.users.setDeleted(data.deleted_users)
  }

  async function fetchUserById(id: number) {
    if (id in cachedUsers) {
      return cachedUsers[id]
    }

    const { data } = await api.get(`users/${id}`)
    cachedUsers[id] = data

    return cachedUsers[id]
  }

  async function forceDelete(id: number) {
    await api.delete(`users/${id}/force`)
    delete deletedUsers[id]
  }

  async function updateRoles({ id, roles }: { id: number; roles: string[] }) {
    const { data } = await api.patch<User>(`user-roles/${id}`, { roles })

    if (id === stores.user.user.id) {
      stores.user._update(data)
    }

    _update(data)
  }

  async function updateFunction(id: number | undefined, organizationFunctionId: number | null) {
    if (!id) {
      return
    }

    await api.patch(`organization-users/${id}`, { organization_function_id: organizationFunctionId })
    users[id].pivot.organization_function_id = organizationFunctionId
  }

  async function remove(payload: { id: number; cases_assignee?: number | null; cases_assignee_type?: string | null; remove_cases: boolean }) {
    const { data } = await api.delete<User>(`users/${payload.id}`, { data: payload })

    const deletedUser = {
      ...users[payload.id],
      ...data,
      pivot: data.pivot,
    }
    delete users[payload.id]
    _updateDeletedUsers(deletedUser)
  }

  async function restore(id: number) {
    const { data } = await api.post<User>(`users/${id}/restore`)

    delete deletedUsers[id]
    _update(data)
  }

  function _updateUsers(updatedUsers: Record<number, Partial<User>>) {
    for (const [id, user] of Object.entries(updatedUsers)) {
      users[Number(id)] = { ...users[Number(id)], ...user }
    }
  }

  function _update(usersToUpdate: AtLeast<User, 'id'> | User[]) {
    if (Array.isArray(usersToUpdate)) {
      for (const user of usersToUpdate) {
        users[user.id] = user
      }
    } else {
      const user = usersToUpdate
      if (user.id in users) {
        Object.assign(users[user.id], user)
      } else {
        users[user.id] = user as User
      }
    }
  }

  function set(usersToSet: User | User[]) {
    for (const key in users) {
      delete users[key]
    }

    Object.assign(users, keyBy('id', Array.isArray(usersToSet) ? usersToSet : [usersToSet]))
  }

  function setDeleted(users: User | User[]) {
    for (const key in deletedUsers) {
      delete deletedUsers[key]
    }

    Object.assign(deletedUsers, keyBy('id', Array.isArray(users) ? users : [users]))
  }

  function _updateDeletedUsers(users: AtLeast<User, 'id'> | User[]) {
    if (Array.isArray(users)) {
      for (const user of users) {
        deletedUsers[user.id] = user
      }
    } else {
      const user = users
      if (user.id in deletedUsers) {
        Object.assign(deletedUsers[user.id], user)
      } else {
        deletedUsers[user.id] = user as User
      }
    }
  }

  return {
    users,
    deletedUsers,
    cachedUsers,
    onlineUsers,
    userIdsWithoutTransactionMonitoringPermission,
    set,
    setDeleted,
    update,
    fetch,
    fetchUserById,
    forceDelete,
    updateRoles,
    updateFunction,
    remove,
    restore,
    _updateUsers,
    _update,
    _updateDeletedUsers,
  }
})

if (import.meta.hot) {
  import.meta.hot.accept(acceptHMRUpdate(useUsers, import.meta.hot))
}
